<template>
  <div v-loading="loading" class="sign-config-page">
    <div class="sign-config-card">
      <div class="card-title">签到配置</div>

      <el-form
        ref="form"
        :model="formData"
        :rules="rules"
        label-width="120px"
        size="mini"
        class="sign-form"
      >
        <el-form-item label="电子票券" prop="ticketId">
          <el-select
            v-model="formData.ticketId"
            filterable
            placeholder="请选择电子票券"
            @change="handleTicketChange"
          >
            <el-option
              v-for="item in ticketOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="连签天数" prop="signDays">
          <el-input-number
            v-model="formData.signDays"
            :min="1"
            :max="30"
            controls-position="right"
          />
          <span class="inline-tip">连续签到达到该天数后发放奖励</span>
        </el-form-item>

        <el-form-item label="顶部图片" prop="topImageUrl">
          <div class="upload-wrap">
            <uploadPic
              ref="uploadPic"
              :my-url="formData.topImageUrl"
              :width="96"
              :height="96"
              :enable-preview-delete="true"
              @upLoadImage="handleUploadSuccess"
            />
          </div>
        </el-form-item>

        <el-form-item label="签到规则" prop="signRule">
          <el-input
            v-model.trim="formData.signRule"
            type="textarea"
            :rows="8"
            placeholder="请输入签到规则"
          />
        </el-form-item>

        <el-form-item label="是否启用" prop="status">
          <el-radio-group v-model="formData.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            class="edit-button"
            size="mini"
            :loading="submitLoading"
            @click="submitForm"
          >
            更新
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import uploadPic from '@/components/Upload/UploadPic'
import { getTicketManList } from '@/api/ticketMan'
import { getSignConfig, saveSignConfig } from '@/api/activity/signConfig'

export default {
  name: 'SignConfig',
  components: { uploadPic },
  data() {
    return {
      loading: false,
      submitLoading: false,
      ticketOptions: [],
      formData: {
        ticketId: '',
        ticketName: '',
        signDays: 7,
        signRule: '',
        topImageUrl: '',
        status: 1
      },
      rules: {
        ticketId: [{ required: true, message: '请选择电子票券', trigger: 'change' }],
        signDays: [{ required: true, message: '请输入连签天数', trigger: 'change' }],
        signRule: [{ required: true, message: '请输入签到规则', trigger: 'blur' }],
        status: [{ required: true, message: '请选择是否启用', trigger: 'change' }]
      }
    }
  },
  mounted() {
    this.initPageData()
  },
  methods: {
    initPageData() {
      this.loading = true
      Promise.all([this.loadTicketOptions(), this.loadConfig()])
        .then(() => {
          this.syncTicketName()
        })
        .catch((err) => {
          this.$message.error(err || '加载失败，请稍后重试')
        })
        .finally(() => {
          this.loading = false
        })
    },
    loadTicketOptions() {
      return getTicketManList().then((res) => {
        const list = Array.isArray(res) ? res : (res && res.rows) || []
        this.ticketOptions = list
          .filter((item) => Number(item.status) === 1)
          .map((item) => ({
            label: item.name,
            value: String(item.id)
          }))
      })
    },
    loadConfig() {
      return getSignConfig().then((res) => {
        if (!res) return
        this.formData = {
          ...this.formData,
          ...res,
          ticketId:
            res.ticketId === undefined || res.ticketId === null
              ? ''
              : String(res.ticketId),
          signDays: Number(res.signDays) > 0 ? Number(res.signDays) : 7,
          status: Number(res.status) === 0 ? 0 : 1
        }
      })
    },
    syncTicketName() {
      const ticket = this.ticketOptions.find(
        (item) => String(item.value) === String(this.formData.ticketId)
      )
      if (ticket) {
        this.formData.ticketName = ticket.label
      } else {
        this.formData.ticketId = ''
        this.formData.ticketName = ''
      }
    },
    handleTicketChange(ticketId) {
      const ticket = this.ticketOptions.find(
        (item) => String(item.value) === String(ticketId)
      )
      this.formData.ticketName = ticket ? ticket.label : ''
    },
    handleUploadSuccess(url) {
      this.formData.topImageUrl = url
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (!valid) return

        const payload = {
          ticketId: Number(this.formData.ticketId),
          ticketName: this.formData.ticketName,
          signDays: Number(this.formData.signDays),
          signRule: this.formData.signRule,
          topImageUrl: this.formData.topImageUrl,
          status: Number(this.formData.status)
        }

        if (!payload.ticketName) {
          this.$message.warning('请选择启用状态的电子票券')
          return
        }
        if (payload.signDays <= 0) {
          this.$message.warning('连签天数必须大于0')
          return
        }

        this.submitLoading = true
        saveSignConfig(payload)
          .then(() => {
            this.$message.success('更新成功')
            this.loadConfig()
          })
          .catch((err) => {
            this.$message.error(err || '更新失败，请稍后重试')
          })
          .finally(() => {
            this.submitLoading = false
          })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.sign-config-page {
  padding: 20px;
  box-sizing: border-box;
}

.sign-config-card {
  width: 880px;
  min-height: 520px;
  padding: 32px 24px;
  box-sizing: border-box;
  background: #f4f4f4;
  border: 1px solid #e8e8e8;
}

.card-title {
  margin-bottom: 26px;
  font-size: 28px;
  font-weight: 700;
  color: #1f1f1f;
}

.sign-form {
  width: 720px;
}

.el-select {
  width: 320px;
}

.upload-wrap {
  display: inline-flex;
}

.inline-tip {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}
</style>
